import { useState } from 'react'

import type {
    PaymentMethodChangeTarget,
    SalesOrderPayment,
} from '@/lib/api/sales-order-api'
import { Button } from '@/components/ui/button'
import {
    ChangePaymentMethodDialog,
    MethodChangeLines,
    isSwitchablePayment,
    paymentMethodLabel,
} from '@/components/orders/change-payment-method-dialog'
import { useChangeOrderPaymentMethod } from '@/hooks/use-sales-order-query'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    orderId: string
    payments: SalesOrderPayment[]
    /** Quyền `customers.payment` — cùng quyền ghi nhận thanh toán. */
    canChangeMethod: boolean
    /** Đơn bán quầy: phiếu thu phần khách đưa dư (nếu có) đổi theo. */
    isQuickSale?: boolean
}

/**
 * Lịch sử thanh toán trong khối Thanh toán của chi tiết đơn. Khoản tiền mặt /
 * chuyển khoản có nút "Đổi hình thức" (M-152); khoản không đổi được vẫn bấm
 * được để xem lý do thay vì nút xám không giải thích.
 */
export function OrderPaymentHistory({
    orderId,
    payments,
    canChangeMethod,
    isQuickSale = false,
}: Props) {
    const [selected, setSelected] = useState<SalesOrderPayment | null>(null)
    const change = useChangeOrderPaymentMethod(orderId)

    if (payments.length === 0) return null

    const confirm = (target: PaymentMethodChangeTarget) => {
        if (!selected) return
        change.mutate(
            { paymentId: selected.id, method: target },
            { onSettled: () => setSelected(null) },
        )
    }

    const note = selected?.receipt_id
        ? m.order_payment_method_change_receipt_note()
        : isQuickSale
          ? m.order_payment_method_change_overpay_note()
          : undefined

    return (
        <div className="mt-3 space-y-2 border-t pt-3">
            {payments.map((p) => (
                <div
                    key={p.id}
                    className="space-y-0.5 text-xs text-muted-foreground"
                >
                    <div className="flex justify-between">
                        <span>
                            {paymentMethodLabel(p.payment_method)} —{' '}
                            {new Date(p.created_at).toLocaleString('vi-VN')}
                        </span>
                        <span className="font-medium">
                            {formatVnd(p.amount)}
                        </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 text-muted-foreground/70">
                        {p.collected_by_driver_name ? (
                            <span>
                                {m.order_collected_by_driver()}:{' '}
                                {p.collected_by_driver_name}
                            </span>
                        ) : (
                            p.created_by_name && (
                                <span>
                                    {m.order_seller()}: {p.created_by_name}
                                </span>
                            )
                        )}
                        {p.reference && (
                            <span>
                                {m.order_transfer_code()}: {p.reference}
                            </span>
                        )}
                        {canChangeMethod &&
                            isSwitchablePayment(p.payment_method) && (
                                <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="h-auto px-0 text-xs md:h-auto md:px-0"
                                    onClick={() => setSelected(p)}
                                >
                                    {m.order_payment_method_change_button()}
                                </Button>
                            )}
                    </div>
                    <MethodChangeLines changes={p.method_changes} />
                </div>
            ))}
            <ChangePaymentMethodDialog
                subject={selected}
                note={note}
                pending={change.isPending}
                onOpenChange={(open) => {
                    if (!open && !change.isPending) setSelected(null)
                }}
                onConfirm={confirm}
            />
        </div>
    )
}
